import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { TableHead, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TableHeadWithTooltip } from "@/components/shared/TableHeadWithTooltip";
import { cn } from "@/lib/utils";
import type { AdPushCutoffsResponse } from "@/types/ad-push";
import { formatDayLabel, METRIC_TOOLTIPS, PRODUCT_COLUMN_TOOLTIP } from "./constants";
import { AD_PUSH_DEFINITION, type AdPushCutoffs } from "./adPush";
import { AdPushRulesPopover } from "./AdPushRulesPopover";

export type ProductSortDirection = "asc" | "desc" | null;

export interface HeatmapHeaderRowProps {
  /** `days` from the daily-ranks response, oldest first -- one column per entry. */
  days: string[];
  /** The current sort state of the one sortable column. `null`/omitted renders the inactive icon --
   * the table shell (WEB-006) owns the actual sort, this row only reflects it. */
  sortDirection?: ProductSortDirection;
  onSortByProduct?: () => void;
  /** The blue custom-cutoffs dot on the sliders button shows only when the brand has saved a triple
   * that differs from the defaults (FR-9's `isCustom`). The table shell reads it from
   * `useGetAdPushCutoffs`. */
  brandHasCustomCutoffs?: boolean;
  /** The Ad-push rules panel's open state and its setter (owned by the table shell). WEB-004 mounts
   * `AdPushRulesPopover` onto the sliders button and binds it to this state (WEB-003's seam). */
  rulesOpen?: boolean;
  onRulesOpenChange?: (open: boolean) => void;
  /** The selected brand and its name — the rules panel's title badge and its PUT target (FR-9, FR-12). */
  brandId?: number | null;
  brandName?: string;
  /** The brand's committed cutoffs + metadata from WEB-003's GET; seeds the panel's draft and its
   * last-changed line. */
  adPushCutoffs?: AdPushCutoffsResponse;
  /** Live-relabel seam (D-6): the panel hands up the valid draft while editing (or `null` for
   * committed) so the table shell can relabel the Ad push column in the browser, no network per
   * keystroke. */
  onAdPushDraftChange?: (cutoffs: AdPushCutoffs | null) => void;
}

function SortAffordanceIcon({ direction }: { direction: ProductSortDirection }) {
  if (direction === "asc") return <ArrowUp className="h-3 w-3" />;
  if (direction === "desc") return <ArrowDown className="h-3 w-3" />;
  return <ArrowUpDown className="h-3 w-3 opacity-40" />;
}

/**
 * `sources.md § 4.6`/measured mockup: every header cell is 12px/600/`text-foreground`
 * (`rgb(78,78,101)`) at a 36px row height (`h-9`); a metric column is right-aligned with 12px
 * padding (`text-right px-3`), a day column is centered with 4px padding (see the day `<TableHead>`
 * below) -- this component covers the metric columns (KPS through Date Added).
 *
 * `width` is the column's own pixel width, `prototype-extracted.md § 1`'s `COL_*` constants --
 * required (not inferred from content) because the table is `table-fixed`: every column takes its
 * width from this first (header) row's cells, and a column with none of its own would instead grab
 * a share of whatever space is left over.
 */
function MetricHead({ label, tooltip, width }: { label: string; tooltip: string; width: number }) {
  return (
    <TableHeadWithTooltip
      style={{ width }}
      className="h-9 px-3 text-right text-xs font-semibold text-foreground whitespace-nowrap"
      tooltip={tooltip}
    >
      {label}
    </TableHeadWithTooltip>
  );
}

/**
 * The heat table's one header row. Column order (`components/rank-tracker.md § Target state -- the
 * screen`, `design-input/rank-tracker-heatmap/sources.md § 4.6`): label · chart (header-less spacer,
 * so `KPS` through `Date Added` stay aligned with the chart column every row carries) · `KPS` ·
 * `Search Vol.` · `Kw. Sales` · `Comp.` · `Rel.` · `Ad push` (AUT-497 slice B) · one column per day ·
 * `Date Added`.
 */
/**
 * `prototype-extracted.md § 1`'s `COL_*` constants, in the same column order this row renders them.
 * The table is `table-fixed` (`RankHeatmapTable/index.tsx`), so this header row -- the table's first
 * row -- is what fixes every column's width; a column left without one here would instead take a
 * share of whatever width is left over, silently squeezing (or stretching) every other column.
 * `COL_LABEL` (340) and `COL_DAY` (54) are applied directly below/on the day `<TableHead>`, not
 * here, since they are not shared across multiple columns the way the metric widths are.
 */
const COL_CHART = 40;
const COL_KPS = 72;
const COL_VOL = 116;
const COL_SALES = 108;
const COL_COMP = 84;
const COL_REL = 72;
const COL_ADPUSH = 140;
const COL_DATE = 104;

export function HeatmapHeaderRow({
  days,
  sortDirection = null,
  onSortByProduct,
  brandHasCustomCutoffs = false,
  rulesOpen = false,
  onRulesOpenChange,
  brandId,
  brandName = "",
  adPushCutoffs,
  onAdPushDraftChange,
}: HeatmapHeaderRowProps) {
  return (
    <TableRow>
      <TableHead sticky style={{ width: 340 }} className="h-9 text-xs font-semibold text-foreground">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onSortByProduct}
              className={cn(
                "inline-flex items-center gap-1 font-semibold text-foreground",
                "cursor-pointer select-none",
              )}
            >
              <span className="border-b border-dotted border-muted-foreground">Product</span>
              <SortAffordanceIcon direction={sortDirection} />
            </button>
          </TooltipTrigger>
          {/* Item 2's same wrap-to-multiple-lines treatment (`max-w-xs text-xs text-left`,
              `TableHeadWithTooltip`'s own `TooltipContent`) applied inline rather than by swapping
              to that shared component: its `TooltipTrigger` is not `asChild`, so it renders its own
              trigger element around whatever `children` it is given -- wrapping this cell's sort
              `<button>` in it would nest a button inside a button. */}
          <TooltipContent side="bottom" className="max-w-xs text-left">
            {PRODUCT_COLUMN_TOOLTIP}
          </TooltipContent>
        </Tooltip>
      </TableHead>

      {/* Header-less spacer for the chart-icon column -- every row carries this column so the
          metric and day columns line up whether or not that row has a chart button. */}
      <TableHead aria-hidden="true" style={{ width: COL_CHART }} className="h-9" />

      <MetricHead label="KPS" tooltip={METRIC_TOOLTIPS.kps} width={COL_KPS} />
      <MetricHead label="Search Vol." tooltip={METRIC_TOOLTIPS.searchVolume} width={COL_VOL} />
      <MetricHead label="Kw. Sales" tooltip={METRIC_TOOLTIPS.keywordSales} width={COL_SALES} />
      <MetricHead label="Comp." tooltip={METRIC_TOOLTIPS.competitors} width={COL_COMP} />
      <MetricHead label="Rel." tooltip={METRIC_TOOLTIPS.relevance} width={COL_REL} />

      {/* The Ad push column (FR-8) -- after Rel., before the day columns, 140px. Left-aligned (it
          holds a pill, not a right-aligned number). The label carries the AD_PUSH_DEFINITION
          tooltip on a dotted-underline cursor-help trigger; `AdPushRulesPopover` mounts on the
          sliders button to its right, bound to the table shell's `rulesOpen` state (WEB-004). */}
      <TableHead
        style={{ width: COL_ADPUSH }}
        className="h-9 px-3 text-xs font-semibold text-foreground whitespace-nowrap"
      >
        <span className="inline-flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger
              className="cursor-help border-b border-dotted border-muted-foreground"
              onClick={(e) => e.preventDefault()}
            >
              Ad push
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-left">{AD_PUSH_DEFINITION}</TooltipContent>
          </Tooltip>
          <AdPushRulesPopover
            brandId={brandId}
            brandName={brandName}
            open={rulesOpen}
            onOpenChange={(next) => onRulesOpenChange?.(next)}
            cutoffs={adPushCutoffs}
            isCustom={brandHasCustomCutoffs}
            onDraftChange={onAdPushDraftChange ?? (() => undefined)}
          />
        </span>
      </TableHead>

      {days.map((day) => (
        <TableHead
          key={day}
          style={{ width: 54 }}
          className="h-9 px-1 text-center text-xs font-semibold text-foreground whitespace-nowrap"
        >
          {formatDayLabel(day)}
        </TableHead>
      ))}

      {/* AUT-526: Date Added is LEFT-aligned (header + values) and its `border-l` draws the thin
          vertical separator between the last date column and this column, matching the prototype. */}
      <TableHeadWithTooltip
        style={{ width: COL_DATE }}
        className="h-9 px-3 text-left text-xs font-semibold text-foreground whitespace-nowrap border-l border-border"
        tooltip={METRIC_TOOLTIPS.dateAdded}
      >
        Date Added
      </TableHeadWithTooltip>
    </TableRow>
  );
}
